/**
 * Shared UFO / Operator Scout radar filter (Favorites 2D map + site pin maps).
 */

export const OPERATOR_SCOUT_SUBTYPES = [
  { id: 'wells', label: 'Wells', emoji: '🛢️', type: 'wells' as const },
  { id: 'swds', label: 'SWD', emoji: '💧', type: 'swds' as const },
  { id: 'rigs', label: 'Rigs', emoji: '🏗️', type: 'rigs' as const },
  { id: 'TB', label: 'Tank Battery', emoji: '🔋', type: 'facilities' as const, facilityTypes: 'TB' },
  { id: 'CS', label: 'Compressor', emoji: '⚙️', type: 'facilities' as const, facilityTypes: 'CS' },
  { id: 'FS', label: 'Flare', emoji: '🔥', type: 'facilities' as const, facilityTypes: 'FS' },
  { id: 'GP', label: 'Gas Plant', emoji: '⛽', type: 'facilities' as const, facilityTypes: 'GP' },
  { id: 'BW', label: 'Brine Well', emoji: '💧', type: 'facilities' as const, facilityTypes: 'BW' },
  { id: 'PIT', label: 'Pit', emoji: '🕳️', type: 'facilities' as const, facilityTypes: 'PIT' },
  { id: 'WH', label: 'Well Head', emoji: '🛢️', type: 'facilities' as const, facilityTypes: 'WH' },
  { id: 'WP', label: 'Well Pad', emoji: '🛢️', type: 'facilities' as const, facilityTypes: 'WP' },
  { id: 'INJ', label: 'Injection', emoji: '💉', type: 'facilities' as const, facilityTypes: 'INJ' },
] as const;

export type OperatorScoutSubtypeId = (typeof OPERATOR_SCOUT_SUBTYPES)[number]['id'];

export const UFO_WELL_STATUS_OPTIONS = [
  {
    id: 'active',
    label: 'Active',
    emoji: '🟢',
    match: ['active', 'producing', 'drilling', 'completed'],
  },
  {
    id: 'permit',
    label: 'Permit / New',
    emoji: '🔵',
    match: ['new', 'permit', 'approved', 'pending', 'extended'],
  },
  {
    id: 'shut-in',
    label: 'Shut-In / Inactive',
    emoji: '🟡',
    match: ['inactive', 'shut', 'suspended', 'idle'],
  },
  {
    id: 'plugged',
    label: 'Plugged / Abandoned',
    emoji: '🔴',
    match: ['plugged', 'abandoned', 'p&a', ' p a ', 'ta'],
  },
  {
    id: 'cancelled',
    label: 'Cancelled / Void',
    emoji: '🟣',
    match: ['cancelled', 'canceled', 'denied', 'expired', 'void'],
  },
  {
    id: 'other',
    label: 'Other / Unknown',
    emoji: '⚪',
    match: [] as string[],
  },
] as const;

export type UfoWellStatusId = (typeof UFO_WELL_STATUS_OPTIONS)[number]['id'];

export type UfoRadarFilter = {
  operator: string;
  subtypeIds: OperatorScoutSubtypeId[];
  statusIds: UfoWellStatusId[];
};

export const EMPTY_UFO_RADAR_FILTER: UfoRadarFilter = {
  operator: '',
  subtypeIds: [],
  statusIds: [],
};

export function isUfoRadarFilterActive(filter: UfoRadarFilter): boolean {
  return Boolean(
    filter.operator.trim()
    || (filter.subtypeIds?.length ?? 0) > 0
    || (filter.statusIds?.length ?? 0) > 0,
  );
}

export function toggleUfoSubtypeId(
  current: OperatorScoutSubtypeId[],
  id: OperatorScoutSubtypeId,
): OperatorScoutSubtypeId[] {
  return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
}

export function toggleUfoStatusId(
  current: UfoWellStatusId[],
  id: UfoWellStatusId,
): UfoWellStatusId[] {
  return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
}

export function classifyUfoWellStatus(status: string | null | undefined): UfoWellStatusId[] {
  const s = String(status || '').toLowerCase().trim();
  if (!s) return ['other'];
  const hits: UfoWellStatusId[] = [];
  for (const opt of UFO_WELL_STATUS_OPTIONS) {
    if (opt.id === 'other') continue;
    const matched = opt.match.some((token) => {
      if (token === 'active') {
        return s.includes('active') && !s.includes('inactive');
      }
      if (token === 'ta') {
        return /\bta\b/.test(s) || s.includes('temporarily abandoned');
      }
      return s.includes(token);
    });
    if (matched) hits.push(opt.id);
  }
  return hits.length ? hits : ['other'];
}

export function facilityMatchesUfoStatuses(
  facility: Record<string, unknown>,
  statusIds: UfoWellStatusId[],
): boolean {
  if (!statusIds.length) return true;
  const buckets = classifyUfoWellStatus(
    (facility.status as string | undefined)
    ?? (facility.wellStatus as string | undefined)
    ?? (facility.well_status as string | undefined),
  );
  return buckets.some((b) => statusIds.includes(b));
}

export function resolveScoutSubtype(
  subtypeId: OperatorScoutSubtypeId | null | undefined,
): {
  type: 'all' | 'wells' | 'swds' | 'rigs' | 'facilities';
  facilityTypes?: string;
  label: string | null;
} {
  if (!subtypeId) return { type: 'all', label: null };
  const subtype = OPERATOR_SCOUT_SUBTYPES.find((s) => s.id === subtypeId);
  if (!subtype) return { type: 'all', label: null };
  return {
    type: subtype.type,
    facilityTypes: 'facilityTypes' in subtype ? subtype.facilityTypes : undefined,
    label: subtype.label,
  };
}

export function formatUfoSubtypeLabels(subtypeIds: OperatorScoutSubtypeId[]): string | null {
  if (!subtypeIds.length) return null;
  const labels = subtypeIds
    .map((id) => resolveScoutSubtype(id).label)
    .filter((label): label is string => Boolean(label));
  if (!labels.length) return null;
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}

export function formatUfoStatusLabels(statusIds: UfoWellStatusId[]): string | null {
  if (!statusIds.length) return null;
  const labels = statusIds
    .map((id) => UFO_WELL_STATUS_OPTIONS.find((o) => o.id === id)?.label)
    .filter((label): label is NonNullable<typeof label> => Boolean(label));
  if (!labels.length) return null;
  if (labels.length <= 3) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}

export function formatUfoRadarFilterLabel(filter: UfoRadarFilter): string {
  const op = filter.operator.trim();
  const sub = formatUfoSubtypeLabels(filter.subtypeIds || []);
  const st = formatUfoStatusLabels(filter.statusIds || []);
  const parts = [op || null, sub, st].filter(Boolean) as string[];
  if (parts.length) return parts.join(' · ');
  return 'All facilities';
}

export function buildUfoFilterSearchParams(filter: UfoRadarFilter): {
  type: string;
  operator?: string;
  facilityTypes?: string;
  needsClientSubtypeFilter: boolean;
} {
  const op = filter.operator.trim();
  const ids = filter.subtypeIds || [];
  if (ids.length === 0) {
    return {
      type: 'all',
      ...(op ? { operator: op } : {}),
      needsClientSubtypeFilter: false,
    };
  }

  const resolved = ids.map((id) => ({ id, ...resolveScoutSubtype(id) }));
  const types = new Set(resolved.map((r) => r.type));
  const facilityCodes = resolved
    .map((r) => r.facilityTypes)
    .filter((code): code is string => Boolean(code));

  if (types.size === 1 && types.has('facilities')) {
    return {
      type: 'facilities',
      ...(op ? { operator: op } : {}),
      facilityTypes: facilityCodes.join(','),
      needsClientSubtypeFilter: facilityCodes.length > 1,
    };
  }
  if (types.size === 1 && !types.has('facilities') && ids.length === 1) {
    return {
      type: resolved[0].type,
      ...(op ? { operator: op } : {}),
      needsClientSubtypeFilter: false,
    };
  }

  return {
    type: 'all',
    ...(op ? { operator: op } : {}),
    needsClientSubtypeFilter: true,
  };
}

function ufoTypeCode(typeStr: string, facilityTypeStr: string): string {
  const lowerType = typeStr.toLowerCase();
  if (lowerType === 'well' || lowerType === 'wells' || lowerType === 'wl') return 'well';
  if (lowerType === 'swd' || lowerType === 'swds') return 'swd';
  if (lowerType === 'rig' || lowerType === 'rigs') return 'rig';
  if (lowerType === 'wp' || lowerType === 'wellpad' || lowerType === 'well_pad') return 'wp';
  if (facilityTypeStr) {
    const match = facilityTypeStr.match(/\(([A-Z0-9]+)\)/);
    if (match) return match[1].toLowerCase();
  }
  return lowerType;
}

export function facilityMatchesUfoSubtypes(
  facility: Record<string, unknown>,
  subtypeIds: OperatorScoutSubtypeId[],
): boolean {
  if (!subtypeIds.length) return true;
  const typeStr = String(facility.type || facility.wellType || '').toLowerCase();
  const facilityTypeStr = String(facility.facilityType || facility.facility_type || '');
  const code = ufoTypeCode(typeStr, facilityTypeStr).toLowerCase();

  return subtypeIds.some((id) => {
    if (id === 'wells') {
      return typeStr.includes('well') && !typeStr.includes('brine') && code !== 'swd' && code !== 'bw';
    }
    if (id === 'swds') return code === 'swd' || typeStr.includes('swd') || typeStr.includes('disposal');
    if (id === 'rigs') return code === 'rig' || typeStr.includes('rig');
    const resolved = resolveScoutSubtype(id);
    const want = (resolved.facilityTypes || id).toLowerCase();
    return code === want || facilityTypeStr.toUpperCase().includes(`(${want.toUpperCase()})`);
  });
}

export function facilityMatchesUfoFilter(
  facility: Record<string, unknown>,
  filter: Pick<UfoRadarFilter, 'subtypeIds' | 'statusIds'>,
  opts?: { needsClientSubtypeFilter?: boolean },
): boolean {
  const needsSubtype = opts?.needsClientSubtypeFilter
    ?? Boolean(filter.subtypeIds?.length);
  if (needsSubtype && !facilityMatchesUfoSubtypes(facility, filter.subtypeIds || [])) {
    return false;
  }
  if (!facilityMatchesUfoStatuses(facility, filter.statusIds || [])) {
    return false;
  }
  return true;
}
